import React, { useEffect, useMemo, useState, useRef, SyntheticEvent, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { DropdownItemType } from '../../models/MessageType'
import { ChevronDown } from '../../icons/Icons'
import { focusTrapCreator } from '../../helper/focusTrapCreator'
import { keyType } from '../../constants/string'

import {
  DropdownSelectButton,
  DropdownSelectContainer,
  DropdownSelectContent,
  DropdownSelectContentContainer,
  DropdownSelectItem,
  DropdownSelectItemButton
} from './DropdownSelect.style'

interface Props {
  items: DropdownItemType[]
  onSelect: (i: DropdownItemType) => void
  isDisabled: boolean
  isLoadingIframeData: boolean
  selectedValue: string
}

export const DropdownSelect: React.FC<Props> = ({ items, onSelect, isDisabled, isLoadingIframeData, selectedValue }) => {
  const [isActive, setIsActive] = useState<boolean>(false)
  const [isTopPosition, setIsTopPosition] = useState<boolean>(true)
  const [contentHeight, setContentHeight] = useState<number>(0)
  const selectButtonRef = useRef<HTMLDivElement>(null)
  const selectContentRef = useRef<HTMLDivElement>(null)
  const selectItemsRef = useRef<HTMLLIElement[]>([])
  const dropdownSelectButtonRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()

  const selectTitle = useMemo(() => selectedValue || t('select'), [selectedValue])

  useEffect(() => {
    if (selectContentRef.current) {
      setContentHeight(selectContentRef.current.clientHeight)
    }
    setTimeout(() => {
      if (isActive) {
        window.addEventListener('click', awayClickListener)
        selectItemsRef.current[0].focus()
      } else {
        window.removeEventListener('click', awayClickListener)
      }
    }, 0)
  }, [isActive])

  const scrollListener = (): void => {
    const outterMargin = 10
    const headerHeight = 54
    const reversLimit = contentHeight + 15
    const { y } = selectButtonRef.current.getBoundingClientRect()
    const pinchPoint = y - (outterMargin + headerHeight)

    if (pinchPoint < reversLimit) {
      setIsTopPosition(false)
    } else {
      setIsTopPosition(true)
    }
  }

  const awayClickListener = (): void => setIsActive(false)

  const selectButtonHandler = (e: SyntheticEvent): void => {
    if (isDisabled) return

    e.stopPropagation()
    scrollListener()
    setIsActive(prevState => !prevState)
  }

  const selectItemHandler = (item: DropdownItemType): void => {
    setIsActive(false)
    onSelect && onSelect(item)
  }

  const keyDownHandler = (e: KeyboardEvent<HTMLLIElement>, item: DropdownItemType): void => {
    const key = e.key.toLowerCase()
    switch (key) {
      case keyType.ENTER:
      case keyType.SPACE:
        selectItemHandler(item)
        setTimeout(() => dropdownSelectButtonRef.current.focus(), 0)
        break
      case keyType.ESCAPE:
        setIsActive(false)
        dropdownSelectButtonRef.current.focus()
        break
    }
  }

  return (
    <DropdownSelectContainer ref={selectButtonRef}>
      <DropdownSelectButton
        ref={dropdownSelectButtonRef}
        onClick={selectButtonHandler}
        isSelected={!!selectedValue}
        isDisabled={isDisabled}
        tabIndex={isLoadingIframeData ? -1 : isDisabled ? -1 : 0}
        aria-haspopup={true}
        aria-expanded={isActive}
        data-test='dropdown-button'
      >
        {selectTitle} {<ChevronDown isDisabled={isDisabled} />}
      </DropdownSelectButton>
      {isActive && (
        <DropdownSelectContent
          ref={selectContentRef}
          isTopPosition={isTopPosition}
          onKeyDown={(e) => focusTrapCreator(e, selectContentRef.current, true)}
        >
          <DropdownSelectContentContainer>
            {items.map((item, index) => (
              <DropdownSelectItem
                key={index}
                ref={el => selectItemsRef.current[index] = el}
                onClick={() => selectItemHandler(item)}
                onKeyDown={(e) => keyDownHandler(e, item)}
                data-test='dropdown-item'
              >
                <DropdownSelectItemButton isLoadingIframeData={isLoadingIframeData}>
                  {item.option}
                </DropdownSelectItemButton>
              </DropdownSelectItem>
            ))}
          </DropdownSelectContentContainer>
        </DropdownSelectContent>
      )}
    </DropdownSelectContainer>
  )
}
